import { ChangeEvent, useContext } from "react";
import { useI18n } from "i18n";
import useCompany from "modules/company/hooks/useCompany";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { SwitchInput } from "@remo-co/ui-core/src/components/SwitchInput";
import { Button } from "@remo-co/ui-core/src/components/Button";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import { updateCompanySettings } from "services/apiService/apis";
import { useSelector } from "react-redux";
import { selectEnableAgencyWhiteLabelling } from "modules/companySettings/redux/selectors";
import { TRACKING_CONTEXT, Events } from "modules/tracking";
import { styles } from "./styles";
import { CompanyWhiteLabelLogo } from "../CompanyWhiteLabelLogo";
import { CompanyWhiteLabelFavicon } from "../CompanyWhiteLabelFavicon";
import { CustomDomain } from "../CustomDomain";
import { SignInImageBackground } from "../SignInImageBackground";
import { DisableFreshchatWidget } from "../DisableFreshchatWidget";
import { EmailSenderSettings } from "../EmailSenderSettings";

interface Props {
  enabledAgencyWhiteLabelling: boolean; // use throughout
}

export const AgencyWhiteLabel = ({ enabledAgencyWhiteLabelling }: Props) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { setCompanySettings } = useCompanySettingsActions();
  const { t } = useI18n(["common", "settings"]);
  const { company } = useCompany();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );

  const saveChanges = async (
    _: ChangeEvent,
    incomingEnableAgencyWhiteLabelling: boolean,
  ) => {
    if (company && company.id) {
      const res = await updateCompanySettings(company.id, {
        enableAgencyWhiteLabelling: incomingEnableAgencyWhiteLabelling,
        company: company.id,
      });

      if (!res.isSuccess) {
        addErrorNotification({
          message: t("agency.white.label.update.error"),
        });

        return;
      }

      if (res.companySettings) {
        setCompanySettings(res.companySettings);
      }
      if (incomingEnableAgencyWhiteLabelling) {
        track(Events.AGENCY_WHITE_LABEL_TOGGLED_ON, {
          companyIds: company.id,
        });
      } else {
        track(Events.AGENCY_WHITE_LABEL_TOGGLED_OFF, {
          companyIds: company.id,
        });
      }
      addSuccessNotification({
        message: t("agency.white.label.update.success"),
      });
    }
  };

  return (
    <Box>
      <Typography variant="h3">{t("settings:custom.branding")}</Typography>
      <Typography sx={styles.helpText} color="textPrimary">
        {t("settings:replace.remo.branding")}
      </Typography>
      {!enabledAgencyWhiteLabelling && (
        <Box sx={styles.upsellContainer}>
          <Typography variant="h5" color="textPrimary" sx={styles.upsellHeader}>
            {t("settings:host.events.branding")}
          </Typography>
          <Typography
            variant="body1"
            color="textPrimary"
            sx={styles.upsellHeader}
          >
            {t("settings:host.events.branding.text")}
          </Typography>
          <Button<"a">
            size="md"
            variant="special"
            href={t("url:contact.sales.mailto")}
            sx={styles.upsellButton}
          >
            {t("settings:contact.sales")}
          </Button>
        </Box>
      )}
      <SwitchInput
        id="white-label-toggle"
        label={t("settings:custom.branding.toggle")}
        checked={
          enabledAgencyWhiteLabelling ? enableAgencyWhiteLabelling : false
        }
        disabled={!enabledAgencyWhiteLabelling}
        onChange={saveChanges}
      />
      <DisableFreshchatWidget
        enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling}
      />
      <CompanyWhiteLabelLogo
        enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling}
      />
      <CompanyWhiteLabelFavicon
        enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling}
      />
      <SignInImageBackground
        enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling}
      />
      <CustomDomain enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling} />
      <EmailSenderSettings
        enabledAgencyWhiteLabelling={enabledAgencyWhiteLabelling}
      />
    </Box>
  );
};
